import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ArrowLeft, FileText, Search, Printer, Download } from "lucide-react";
import { useNavigate } from "react-router-dom";
import html2pdf from "html2pdf.js";

// Iconos de alérgenos en SVG
const ALLERGEN_ICONS = {
  moluscos: (
    <svg viewBox="0 0 100 100" className="w-12 h-12">
      <circle cx="50" cy="50" r="45" fill="#8B4513" stroke="#5D3A1A" strokeWidth="3"/>
      <ellipse cx="50" cy="50" rx="30" ry="20" fill="#D2691E"/>
      <ellipse cx="50" cy="45" rx="15" ry="10" fill="#F4A460"/>
      <text x="50" y="85" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">MOLUSCOS</text>
    </svg>
  ),
  crustaceos: (
    <svg viewBox="0 0 100 100" className="w-12 h-12">
      <circle cx="50" cy="50" r="45" fill="#DC143C" stroke="#8B0000" strokeWidth="3"/>
      <ellipse cx="50" cy="45" rx="25" ry="15" fill="#FF6347"/>
      <circle cx="40" cy="40" r="4" fill="black"/>
      <circle cx="60" cy="40" r="4" fill="black"/>
      <path d="M 30 55 Q 50 70 70 55" stroke="#8B0000" strokeWidth="3" fill="none"/>
      <text x="50" y="85" textAnchor="middle" fill="white" fontSize="8" fontWeight="bold">CRUSTÁCEOS</text>
    </svg>
  ),
  pescado: (
    <svg viewBox="0 0 100 100" className="w-12 h-12">
      <circle cx="50" cy="50" r="45" fill="#4169E1" stroke="#00008B" strokeWidth="3"/>
      <ellipse cx="50" cy="45" rx="30" ry="15" fill="#87CEEB"/>
      <polygon points="75,45 90,35 90,55" fill="#87CEEB"/>
      <circle cx="35" cy="42" r="4" fill="black"/>
      <text x="50" y="85" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">PESCADO</text>
    </svg>
  ),
  gluten: (
    <svg viewBox="0 0 100 100" className="w-12 h-12">
      <circle cx="50" cy="50" r="45" fill="#DAA520" stroke="#B8860B" strokeWidth="3"/>
      <ellipse cx="50" cy="40" rx="12" ry="25" fill="#F5DEB3"/>
      <ellipse cx="35" cy="50" rx="8" ry="15" fill="#F5DEB3" transform="rotate(-20 35 50)"/>
      <ellipse cx="65" cy="50" rx="8" ry="15" fill="#F5DEB3" transform="rotate(20 65 50)"/>
      <text x="50" y="85" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">GLUTEN</text>
    </svg>
  ),
  huevos: (
    <svg viewBox="0 0 100 100" className="w-12 h-12">
      <circle cx="50" cy="50" r="45" fill="#FFD700" stroke="#FFA500" strokeWidth="3"/>
      <ellipse cx="50" cy="50" rx="20" ry="28" fill="#FFFACD"/>
      <ellipse cx="50" cy="50" rx="10" ry="10" fill="#FFD700"/>
      <text x="50" y="90" textAnchor="middle" fill="#8B4513" fontSize="10" fontWeight="bold">HUEVOS</text>
    </svg>
  ),
  lacteos: (
    <svg viewBox="0 0 100 100" className="w-12 h-12">
      <circle cx="50" cy="50" r="45" fill="#F0F8FF" stroke="#4682B4" strokeWidth="3"/>
      <path d="M 35 30 L 35 65 Q 35 75 50 75 Q 65 75 65 65 L 65 30 Z" fill="#FFFAFA" stroke="#4682B4" strokeWidth="2"/>
      <rect x="30" y="25" width="40" height="10" fill="#4682B4"/>
      <text x="50" y="90" textAnchor="middle" fill="#4682B4" fontSize="10" fontWeight="bold">LÁCTEOS</text>
    </svg>
  ),
  soja: (
    <svg viewBox="0 0 100 100" className="w-12 h-12">
      <circle cx="50" cy="50" r="45" fill="#90EE90" stroke="#228B22" strokeWidth="3"/>
      <ellipse cx="40" cy="45" rx="12" ry="8" fill="#98FB98"/>
      <ellipse cx="60" cy="45" rx="12" ry="8" fill="#98FB98"/>
      <ellipse cx="50" cy="55" rx="12" ry="8" fill="#98FB98"/>
      <text x="50" y="85" textAnchor="middle" fill="#006400" fontSize="12" fontWeight="bold">SOJA</text>
    </svg>
  ),
  default: (
    <svg viewBox="0 0 100 100" className="w-12 h-12">
      <circle cx="50" cy="50" r="45" fill="#FF4500" stroke="#8B0000" strokeWidth="3"/>
      <text x="50" y="55" textAnchor="middle" fill="white" fontSize="30" fontWeight="bold">!</text>
      <text x="50" y="85" textAnchor="middle" fill="white" fontSize="8" fontWeight="bold">ALÉRGENO</text>
    </svg>
  )
};

// Función para obtener el icono de alérgeno
const getAllergenIcon = (allergenText) => {
  if (!allergenText) return null;
  const lower = allergenText.toLowerCase();
  
  if (lower.includes("molus")) return { icon: ALLERGEN_ICONS.moluscos, name: "MOLUSCOS" };
  if (lower.includes("crust")) return { icon: ALLERGEN_ICONS.crustaceos, name: "CRUSTÁCEOS" };
  if (lower.includes("pescado") || lower.includes("fish")) return { icon: ALLERGEN_ICONS.pescado, name: "PESCADO" };
  if (lower.includes("gluten") || lower.includes("trigo") || lower.includes("cereal")) return { icon: ALLERGEN_ICONS.gluten, name: "GLUTEN" };
  if (lower.includes("huevo") || lower.includes("egg")) return { icon: ALLERGEN_ICONS.huevos, name: "HUEVOS" };
  if (lower.includes("leche") || lower.includes("lácteo") || lower.includes("lacteo") || lower.includes("milk")) return { icon: ALLERGEN_ICONS.lacteos, name: "LÁCTEOS" };
  if (lower.includes("soja") || lower.includes("soy")) return { icon: ALLERGEN_ICONS.soja, name: "SOJA" };
  
  return { icon: ALLERGEN_ICONS.default, name: allergenText.toUpperCase() };
};

export default function Traceability() {
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const printRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [generating, setGenerating] = useState(false);
  
  // Filtros
  const [filters, setFilters] = useState({
    numero_lote: "",
    contenedor: "",
    bl: "",
    fecha_desde: "",
    fecha_hasta: "",
    producto_id: "",
    factura_origen: ""
  });
  
  // Datos auxiliares
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [logistics, setLogistics] = useState([]);
  const [empresaActiva, setEmpresaActiva] = useState(null);
  const [config, setConfig] = useState(null);
  
  // Cargar datos iniciales
  useEffect(() => {
    const loadData = async () => {
      try {
        const [productsRes, clientsRes, suppliersRes, logisticsRes, configRes] = await Promise.all([
          api.get("/products"),
          api.get("/clients"),
          api.get("/suppliers"),
          api.get("/logistics"),
          api.get("/config")
        ]);
        setProducts(productsRes.data);
        setClients(clientsRes.data);
        setSuppliers(suppliersRes.data);
        setLogistics(logisticsRes.data);
        setConfig(configRes.data);
        
        // Obtener empresa activa
        const config = configRes.data;
        const empresas = config.empresas || [];
        const empresa = empresas.find(e => e.id === config.empresa_activa_id);
        setEmpresaActiva(empresa);
      } catch (error) {
        console.error("Error cargando datos:", error);
      }
    };
    loadData();
  }, []);
  
  // Buscar lotes
  const handleSearch = async () => {
    setLoading(true);
    setSelectedBatch(null);
    try {
      // Obtener todos los lotes y filtrar en el frontend
      const res = await api.get('/batches');
      let filteredBatches = res.data;
      
      // Filtrar por número de lote
      if (filters.numero_lote) {
        const searchTerm = filters.numero_lote.toLowerCase();
        filteredBatches = filteredBatches.filter(b => 
          b.numero_lote?.toLowerCase().includes(searchTerm)
        );
      }
      
      // Filtrar por contenedor
      if (filters.contenedor) {
        const searchTerm = filters.contenedor.toLowerCase();
        filteredBatches = filteredBatches.filter(b => 
          b.contenedor?.toLowerCase().includes(searchTerm) ||
          b.awb_aereo?.toLowerCase().includes(searchTerm)
        );
      }
      
      // Filtrar por BL
      if (filters.bl) {
        const searchTerm = filters.bl.toLowerCase();
        filteredBatches = filteredBatches.filter(b => 
          b.bl_maritimo?.toLowerCase().includes(searchTerm)
        );
      }
      
      // Filtrar por factura origen
      if (filters.factura_origen) {
        const searchTerm = filters.factura_origen.toLowerCase();
        filteredBatches = filteredBatches.filter(b => 
          b.factura_origen?.toLowerCase().includes(searchTerm)
        );
      }
      
      // Filtrar por producto
      if (filters.producto_id) {
        filteredBatches = filteredBatches.filter(b => 
          b.producto_id === filters.producto_id
        );
      }
      
      // Filtrar por fecha desde
      if (filters.fecha_desde) {
        const fechaDesde = new Date(filters.fecha_desde);
        filteredBatches = filteredBatches.filter(b => {
          if (!b.llegada_estimada) return false;
          return new Date(b.llegada_estimada) >= fechaDesde;
        });
      }
      
      // Filtrar por fecha hasta
      if (filters.fecha_hasta) {
        const fechaHasta = new Date(filters.fecha_hasta);
        filteredBatches = filteredBatches.filter(b => {
          if (!b.llegada_estimada) return false;
          return new Date(b.llegada_estimada) <= fechaHasta;
        });
      }
      
      setBatches(filteredBatches);
      
      if (filteredBatches.length === 0) {
        toast.info("No se encontraron lotes con los filtros especificados");
      }
    } catch (error) {
      toast.error("Error al buscar lotes");
    } finally {
      setLoading(false);
    }
  };
  
  // Seleccionar lote
  const handleSelectBatch = async (batch) => {
    setSelectedBatch(batch);
  };
  
  // Obtener datos completos para el documento
  const getTraceabilityData = () => {
    if (!selectedBatch) return null;
    
    const batch = selectedBatch;
    const product = products.find(p => p.id === batch.producto_id);
    const client = clients.find(c => c.id === batch.cliente_id);
    const supplier = suppliers.find(s => s.id === batch.proveedor_id);
    const logistica = logistics.find(l => l.id === batch.logistica_id);
    
    // Calcular kilos de plástico (fórmula: peso_neto * 0.003630425)
    const pesoNeto = batch.peso_neto || 0;
    const kilosPlastico = (pesoNeto * 0.003630425).toFixed(2);
    
    // Formatear fecha
    const formatDate = (dateStr) => {
      if (!dateStr) return "-";
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString("es-ES");
      } catch {
        return dateStr;
      }
    };
    
    return {
      // Empresa importadora
      empresa: empresaActiva,
      
      // Producto
      producto: product?.nombre || batch.producto_nombre || "PRODUCTO",
      
      // Identificadores
      lote: batch.numero_lote,
      contenedor: batch.contenedor || batch.awb_aereo || "",
      bl: batch.bl_maritimo || "",
      fecha: formatDate(batch.llegada_estimada),
      
      // Intervinientes - TODOS LOS DATOS
      cliente: client ? {
        nombre: client.nombre || "",
        cif: client.identificacion_fiscal || client.cif || "",
        direccion: client.direccion || "",
        ciudad: client.ciudad || "",
        pais: client.pais || "",
        codigo_postal: client.codigo_postal || "",
        telefono: client.telefono || "",
        email: client.email || "",
        persona_contacto: client.persona_contacto || ""
      } : null,
      
      proveedor: supplier ? {
        nombre: supplier.nombre || "",
        cif: supplier.identificacion_fiscal || supplier.rif || "",
        direccion: supplier.direccion || "",
        ciudad: supplier.ciudad || "",
        pais: supplier.pais || "",
        codigo_postal: supplier.codigo_postal || "",
        telefono: supplier.telefono || "",
        email: supplier.email || "",
        persona_contacto: supplier.persona_contacto || ""
      } : null,
      
      logistica: logistica ? {
        nombre: logistica.nombre || "",
        cif: logistica.identificacion_fiscal || logistica.cif || "",
        direccion: logistica.direccion || "",
        telefono: logistica.telefono || "",
        email: logistica.email || ""
      } : null,
      
      // Productor (de configuración)
      productor: {
        nombre: config?.productor_nombre || "",
        rif: config?.productor_rif || "",
        spes: config?.productor_spes || "",
        direccion: config?.productor_direccion || "",
        pais: config?.productor_pais || "",
        codigo_iso: config?.productor_codigo_iso || ""
      },
      
      // Especificaciones
      nombre_cientifico: product?.nombre_cientifico || batch.nombre_cientifico || "",
      zona_fao: batch.zona_fao || product?.zona_fao || "",
      arte_pesca: batch.arte_pesca || product?.arte_pesca || "",
      codigo_alfa: product?.codigo_alfa || batch.codigo_alfa || "",
      peso_neto: pesoNeto,
      peso_bruto: batch.peso_bruto || 0,
      registro_sanitario: empresaActiva?.rgseaa || "",
      presentacion: batch.presentacion || product?.presentacion || "",
      puerto_origen: product?.puerto_desembarco || batch.puerto_origen || "",
      pais_origen: batch.pais_origen || product?.pais_origen || "",
      fecha_captura: formatDate(batch.fecha_captura),
      fecha_caducidad: formatDate(batch.fecha_caducidad),
      
      // Composición
      kilos_plastico: kilosPlastico,
      
      // Alérgenos
      alergenos: product?.alergenos || batch.alergenos || ""
    };
  };
  
  // Generar PDF
  const handleGeneratePDF = async () => {
    if (!selectedBatch) {
      toast.error("Selecciona un lote primero");
      return;
    }
    
    setGenerating(true);
    try {
      const element = printRef.current;
      if (!element) {
        toast.error("Error al generar el documento");
        return;
      }
      
      const data = getTraceabilityData();
      const filename = `Trazabilidad_${data.lote}_${new Date().toISOString().slice(0,10)}.pdf`;
      
      const opt = {
        margin: [5, 8, 5, 8], // top, left, bottom, right
        filename: filename,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: 'avoid-all' }
      };
      
      await html2pdf().set(opt).from(element).save();
      toast.success("Documento de trazabilidad generado");
    } catch (error) {
      console.error("Error generando PDF:", error);
      toast.error("Error al generar el documento");
    } finally {
      setGenerating(false);
    }
  };
  
  // Imprimir
  const handlePrint = () => {
    window.print();
  };
  
  const data = getTraceabilityData();
  const allergenInfo = data ? getAllergenIcon(data.alergenos) : null;
  
  // Verificar permisos
  if (!hasRole("admin") && !hasRole("admin_consultor")) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-600">No tienes permisos para acceder a esta sección</p>
      </div>
    );
  }
  
  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate("/reports")}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            Volver
          </Button>
          <h1 className="text-2xl font-bold text-slate-800">Trazabilidad Sanitaria</h1>
        </div>
        
        {selectedBatch && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={handlePrint}>
              <Printer className="w-4 h-4 mr-2" />
              Imprimir
            </Button>
            <Button 
              onClick={handleGeneratePDF} 
              disabled={generating}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Download className="w-4 h-4 mr-2" />
              {generating ? "Generando..." : "Generar Trazabilidad"}
            </Button>
          </div>
        )}
      </div>
      
      {/* Filtros de búsqueda */}
      <Card className="mb-6 no-print">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Search className="w-5 h-5" />
            Buscar Lote
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="space-y-2">
              <Label>Nº Lote</Label>
              <Input
                placeholder="PUC260113B01"
                value={filters.numero_lote}
                onChange={(e) => setFilters({...filters, numero_lote: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Contenedor</Label>
              <Input
                placeholder="SEKU9135382"
                value={filters.contenedor}
                onChange={(e) => setFilters({...filters, contenedor: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>BL</Label>
              <Input
                placeholder="VLN0147183"
                value={filters.bl}
                onChange={(e) => setFilters({...filters, bl: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Factura Origen</Label>
              <Input
                placeholder="20260113001"
                value={filters.factura_origen}
                onChange={(e) => setFilters({...filters, factura_origen: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Fecha Desde</Label>
              <Input
                type="date"
                value={filters.fecha_desde}
                onChange={(e) => setFilters({...filters, fecha_desde: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Fecha Hasta</Label>
              <Input
                type="date"
                value={filters.fecha_hasta}
                onChange={(e) => setFilters({...filters, fecha_hasta: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Producto</Label>
              <Select 
                value={filters.producto_id || "all"} 
                onValueChange={(v) => setFilters({...filters, producto_id: v === "all" ? "" : v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {products.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button onClick={handleSearch} disabled={loading} className="w-full">
                <Search className="w-4 h-4 mr-2" />
                {loading ? "Buscando..." : "Buscar"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Lista de lotes encontrados */}
      {batches.length > 0 && (
        <Card className="mb-6 no-print">
          <CardHeader>
            <CardTitle className="text-lg">Lotes encontrados ({batches.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-h-64 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Lote</TableHead>
                    <TableHead>Producto</TableHead>
                    <TableHead>Contenedor</TableHead>
                    <TableHead>BL</TableHead>
                    <TableHead>Llegada</TableHead>
                    <TableHead>Acción</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {batches.map(batch => (
                    <TableRow 
                      key={batch.id} 
                      className={selectedBatch?.id === batch.id ? "bg-emerald-50" : "hover:bg-slate-50 cursor-pointer"}
                      onClick={() => handleSelectBatch(batch)}
                    >
                      <TableCell className="font-mono font-medium">{batch.numero_lote}</TableCell>
                      <TableCell>{batch.producto_nombre}</TableCell>
                      <TableCell className="font-mono text-sm">{batch.contenedor || batch.awb_aereo || "-"}</TableCell>
                      <TableCell className="font-mono text-sm">{batch.bl_maritimo || "-"}</TableCell>
                      <TableCell>{batch.llegada_estimada ? new Date(batch.llegada_estimada).toLocaleDateString("es-ES") : "-"}</TableCell>
                      <TableCell>
                        <Button 
                          size="sm" 
                          variant={selectedBatch?.id === batch.id ? "default" : "outline"}
                          onClick={(e) => { e.stopPropagation(); handleSelectBatch(batch); }}
                        >
                          {selectedBatch?.id === batch.id ? "Seleccionado" : "Seleccionar"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Vista previa del documento */}
      {selectedBatch && data && (
        <div ref={printRef} className="bg-white p-6 shadow-lg border rounded-lg print-content" style={{maxWidth: '210mm', minHeight: 'auto', margin: '0 auto'}}>
          {/* Header con logo y datos empresa */}
          <div className="flex justify-between items-start mb-4 border-b-2 border-emerald-600 pb-3">
            <div className="flex items-center gap-3">
              {data.empresa?.logo_url && (
                <img 
                  src={`${window.location.origin}${data.empresa.logo_url}`} 
                  alt="Logo" 
                  className="h-14 object-contain"
                  crossOrigin="anonymous"
                />
              )}
              <div>
                <h1 className="text-xl font-bold text-emerald-800">TRAZABILIDAD SANITARIA</h1>
                <p className="text-base font-semibold text-slate-700">{data.producto}</p>
              </div>
            </div>
            <div className="text-right text-xs">
              <p className="font-bold text-slate-800">{data.empresa?.nombre || "EMPRESA"}</p>
              <p className="text-slate-600">CIF: {data.empresa?.cif || ""}</p>
              <p className="text-slate-600">{data.empresa?.direccion || ""}</p>
              <p className="text-slate-600">{data.empresa?.email || ""}</p>
            </div>
          </div>
          
          {/* Identificador de Lote - CENTRADO Y MUY GRANDE (30px) */}
          <div className="text-center mb-3 py-3 bg-emerald-50 rounded-lg">
            <p className="text-xs text-slate-500 uppercase tracking-wider">Identificador de Lote</p>
            <p className="font-bold font-mono text-emerald-800" style={{fontSize: '30px', lineHeight: '1.2'}}>{data.lote}</p>
          </div>
          
          {/* Contenedor y Fecha - Una línea */}
          <div className="text-center mb-4 text-sm">
            <span className="text-slate-600">Contenedor / AWB:</span>
            <span className="font-mono font-semibold ml-2">{data.bl} {data.contenedor}</span>
            <span className="mx-4 text-slate-300">|</span>
            <span className="text-slate-600">Fecha Llegada:</span>
            <span className="font-semibold ml-2">{data.fecha}</span>
          </div>
          
          {/* Intervinientes - TODOS LOS DATOS */}
          <div className="mb-4">
            <h2 className="text-sm font-bold text-emerald-800 border-b border-emerald-200 pb-1 mb-2">
              DATOS DE INTERVINIENTES
            </h2>
            <div className="grid grid-cols-4 gap-2">
              {/* Cliente */}
              {data.cliente && (
                <div className="bg-blue-50 p-2 rounded text-[10px]">
                  <p className="font-bold text-blue-800 uppercase mb-1">Cliente</p>
                  <p className="font-semibold">{data.cliente.nombre}</p>
                  <p className="text-slate-600">CIF: {data.cliente.cif}</p>
                  <p className="text-slate-600">{data.cliente.direccion}</p>
                  {data.cliente.ciudad && <p className="text-slate-600">{data.cliente.codigo_postal} {data.cliente.ciudad}</p>}
                  {data.cliente.pais && <p className="text-slate-600">{data.cliente.pais}</p>}
                  {data.cliente.telefono && <p className="text-slate-600">Tel: {data.cliente.telefono}</p>}
                  {data.cliente.email && <p className="text-slate-600">{data.cliente.email}</p>}
                  {data.cliente.persona_contacto && <p className="text-slate-600">Contacto: {data.cliente.persona_contacto}</p>}
                </div>
              )}
              
              {/* Proveedor */}
              {data.proveedor && (
                <div className="bg-amber-50 p-2 rounded text-[10px]">
                  <p className="font-bold text-amber-800 uppercase mb-1">Proveedor</p>
                  <p className="font-semibold">{data.proveedor.nombre}</p>
                  <p className="text-slate-600">CIF: {data.proveedor.cif}</p>
                  <p className="text-slate-600">{data.proveedor.direccion}</p>
                  {data.proveedor.ciudad && <p className="text-slate-600">{data.proveedor.codigo_postal} {data.proveedor.ciudad}</p>}
                  {data.proveedor.pais && <p className="text-slate-600">{data.proveedor.pais}</p>}
                  {data.proveedor.telefono && <p className="text-slate-600">Tel: {data.proveedor.telefono}</p>}
                  {data.proveedor.email && <p className="text-slate-600">{data.proveedor.email}</p>}
                  {data.proveedor.persona_contacto && <p className="text-slate-600">Contacto: {data.proveedor.persona_contacto}</p>}
                </div>
              )}
              
              {/* Logística */}
              {data.logistica && (
                <div className="bg-purple-50 p-2 rounded text-[10px]">
                  <p className="font-bold text-purple-800 uppercase mb-1">Op. Logístico</p>
                  <p className="font-semibold">{data.logistica.nombre}</p>
                  <p className="text-slate-600">CIF: {data.logistica.cif}</p>
                  <p className="text-slate-600">{data.logistica.direccion}</p>
                  {data.logistica.telefono && <p className="text-slate-600">Tel: {data.logistica.telefono}</p>}
                  {data.logistica.email && <p className="text-slate-600">{data.logistica.email}</p>}
                </div>
              )}
              
              {/* Productor */}
              {data.productor?.nombre && (
                <div className="bg-green-50 p-2 rounded text-[10px]">
                  <p className="font-bold text-green-800 uppercase mb-1">Producido Por</p>
                  <p className="font-semibold">{data.productor.nombre}</p>
                  {data.productor.rif && <p className="text-slate-600">RIF: {data.productor.rif}</p>}
                  {data.productor.spes && <p className="text-slate-600">SPES: {data.productor.spes}</p>}
                  {data.productor.direccion && <p className="text-slate-600">{data.productor.direccion}</p>}
                  {data.productor.pais && <p className="text-slate-600">{data.productor.pais} {data.productor.codigo_iso ? `(${data.productor.codigo_iso})` : ''}</p>}
                </div>
              )}
            </div>
          </div>
          
          {/* Especificaciones - Formato mejorado */}
          <div className="mb-4">
            <h2 className="text-sm font-bold text-emerald-800 border-b border-emerald-200 pb-1 mb-2">
              ESPECIFICACIONES
            </h2>
            <div className="grid grid-cols-2 gap-x-6 text-xs">
              <div className="py-1 border-b border-slate-100">
                <span className="text-slate-500">Presentación:</span>
                <span className="font-semibold ml-2">{data.presentacion || "-"}</span>
              </div>
              <div className="py-1 border-b border-slate-100">
                <span className="text-slate-500">Temperatura:</span>
                <span className="font-semibold ml-2">Mantener congelado a -18º C</span>
              </div>
              <div className="py-1 border-b border-slate-100">
                <span className="text-slate-500">Zona FAO:</span>
                <span className="font-semibold ml-2">{data.zona_fao || "-"}</span>
              </div>
              <div className="py-1 border-b border-slate-100">
                <span className="text-slate-500">Nombre científico:</span>
                <span className="font-semibold ml-2">{data.nombre_cientifico || "-"}</span>
              </div>
              <div className="py-1 border-b border-slate-100">
                <span className="text-slate-500">Arte de pesca:</span>
                <span className="font-semibold ml-2">{data.arte_pesca || "-"}</span>
              </div>
              <div className="py-1 border-b border-slate-100">
                <span className="text-slate-500">Código Alfa:</span>
                <span className="font-semibold ml-2">{data.codigo_alfa || "-"}</span>
              </div>
              <div className="py-1 border-b border-slate-100">
                <span className="text-slate-500">Puerto desembarco:</span>
                <span className="font-semibold ml-2">{data.puerto_origen || "-"}</span>
              </div>
              <div className="py-1 border-b border-slate-100">
                <span className="text-slate-500">País origen:</span>
                <span className="font-semibold ml-2">{data.pais_origen || "-"}</span>
              </div>
              <div className="py-1 border-b border-slate-100">
                <span className="text-slate-500">Reg. sanitario importador:</span>
                <span className="font-semibold ml-2">{data.registro_sanitario || "-"}</span>
              </div>
              <div className="py-1 border-b border-slate-100">
                <span className="text-slate-500">Peso Neto:</span>
                <span className="font-semibold font-mono ml-2">{data.peso_neto?.toLocaleString('es-ES', {minimumFractionDigits: 2})} kg</span>
              </div>
              <div className="py-1 col-span-2">
                <span className="text-slate-500">Fecha captura:</span>
                <span className="font-semibold ml-2">{data.fecha_captura}</span>
                <span className="mx-3 text-slate-300">|</span>
                <span className="text-slate-500">Caducidad:</span>
                <span className="font-semibold ml-2">{data.fecha_caducidad}</span>
              </div>
            </div>
          </div>
          
          {/* Composición y Alérgenos */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <h2 className="text-sm font-bold text-emerald-800 border-b border-emerald-200 pb-1 mb-2">
                COMPOSICIÓN
              </h2>
              <div className="bg-slate-50 p-3 rounded text-xs">
                <p className="font-semibold">{data.producto}</p>
                <p className="text-slate-600 mt-1">
                  Plástico: <span className="font-mono">{data.kilos_plastico} kg</span>
                </p>
              </div>
            </div>
            
            <div>
              <h2 className="text-sm font-bold text-emerald-800 border-b border-emerald-200 pb-1 mb-2">
                ALÉRGENO
              </h2>
              <div className="bg-red-50 p-3 rounded flex items-center gap-3">
                {allergenInfo ? (
                  <>
                    <div className="flex-shrink-0" style={{width: '40px', height: '40px'}}>
                      {allergenInfo.icon}
                    </div>
                    <div>
                      <p className="font-bold text-red-800 text-sm">{allergenInfo.name}</p>
                      <p className="text-xs text-red-600">Contiene alérgenos</p>
                    </div>
                  </>
                ) : (
                  <p className="text-slate-500 text-xs">Sin alérgenos declarados</p>
                )}
              </div>
            </div>
          </div>
          
          {/* Footer */}
          <div className="border-t border-emerald-600 pt-2 mt-4">
            <div className="flex justify-between items-end text-[10px] text-slate-500">
              <div>
                <p>{data.empresa?.nombre} {data.empresa?.cif}. {data.empresa?.registro_mercantil || ""}</p>
              </div>
              <div className="text-right">
                <p>Fecha de impresión: {new Date().toLocaleDateString("es-ES")}</p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Mensaje si no hay lote seleccionado */}
      {!selectedBatch && batches.length === 0 && (
        <Card className="no-print">
          <CardContent className="py-12 text-center">
            <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Busca un lote para generar su documento de trazabilidad</p>
          </CardContent>
        </Card>
      )}
      
      {/* Estilos de impresión */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-content { 
            box-shadow: none !important; 
            border: none !important;
            padding: 0 !important;
          }
          @page {
            size: A4;
            margin: 10mm;
          }
        }
      `}</style>
    </div>
  );
}
